import React, { useEffect, useState, useRef } from 'react';
import { getDolarBlue, getDolarCripto } from '../../../../services/useAPICaller';
import { WizardData } from '../NewPOSWizard';
import { popularCurrencies, otherCurrencies } from '../../../../variables/currencies';
import { EXCHANGE_RATE_TYPES } from '../../../../constants/exchangeRates';
import { useI18n } from '../../../../i18n/I18n';

interface Step2Props {
    formData: WizardData;
    setFormData: React.Dispatch<React.SetStateAction<WizardData>>;
    onNext: () => void;
    onBack: () => void;
}

export default function Step2Currency({ formData, setFormData, onNext, onBack }: Step2Props) {
    const { t } = useI18n();
    const [dolarBlue, setDolarBlue] = useState<any | null>(null);
    const [dolarCripto, setDolarCripto] = useState<any | null>(null);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const currencies = [...popularCurrencies, ...otherCurrencies].map(c => c.value);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Fetch exchange rates on mount
    useEffect(() => {
        const fetchRates = async () => {
            const [blue, cripto] = await Promise.all([getDolarBlue(), getDolarCripto()]);
            if (blue) setDolarBlue(blue);
            if (cripto) setDolarCripto(cripto);
        };
        fetchRates();
    }, []);

    const handleAdjustmentChange = (val: number) => {
        setFormData(prev => ({
            ...prev,
            customExchangeRate: parseFloat((prev.customExchangeRate + val).toFixed(1))
        }));
    };

    const getUpdatedTimeAgo = (dateString: string) => {
        if (!dateString) return '';
        const updated = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - updated.getTime();
        const diffMins = Math.floor(diffMs / 60000);

        const isToday = updated.getDate() === now.getDate() && updated.getMonth() === now.getMonth() && updated.getFullYear() === now.getFullYear();

        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const isYesterday = updated.getDate() === yesterday.getDate() && updated.getMonth() === yesterday.getMonth() && updated.getFullYear() === yesterday.getFullYear();

        const timeStr = updated.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

        if (isToday) {
            if (diffMins <= 60) {
                return `${t('wizard.updatedAgo')} ${Math.max(1, diffMins)} ${t('wizard.minutes')}`;
            } else {
                const h = Math.floor(diffMins / 60);
                const m = diffMins % 60;
                return m > 0 ? `${t('wizard.updatedAgo')} ${h}h ${t('wizard.and')} ${m} ${t('wizard.minutes')}` : `${t('wizard.updatedAgo')} ${h}h`;
            }
        } else if (isYesterday) {
            return `${t('wizard.yesterday')} ${t('wizard.at')} ${timeStr}`;
        } else {
            const monthsList = [t('months.jan'), t('months.feb'), t('months.mar'), t('months.apr'), t('months.may'), t('months.jun'), t('months.jul'), t('months.aug'), t('months.sep'), t('months.oct'), t('months.nov'), t('months.dec')];
            return `${t('wizard.on')} ${updated.getDate()} ${t('wizard.of')} ${monthsList[updated.getMonth()]} ${t('wizard.at')} ${timeStr}`;
        }
    };

    const getBaseRate = () => {
        if (formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE) return dolarBlue?.venta || 0;
        if (formData.exchangeRateType === EXCHANGE_RATE_TYPES.CRIPTO) return dolarCripto?.venta || 0;
        return formData.customExchangeRate || 0;
    };

    const calculateFinalRate = () => {
        const base = getBaseRate();
        const adj = base * (formData.customExchangeRate / 100);
        return Math.round(base + adj);
    };

    return (
        <div className="flex-1 flex flex-col min-h-0 h-full w-full animate-in fade-in slide-in-from-right duration-300">
            {/* Header Area */}
            <div className="mb-4 flex-shrink-0 px-6">
                <h2 className="text-2xl font-bold text-txt-heading mb-2">{t('wizard.step2Title')}</h2>
                <div className="flex flex-col gap-2">
                    <p className="text-txt-secondary text-sm">{t('wizard.step2Subtitle')}</p>
                    <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: 'var(--card-bg)' }}>
                        <div className="h-full bg-electric-blue w-full rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content + Sticky Actions */}
            <div className="flex-1 overflow-y-auto w-full no-scrollbar">
                <div className="space-y-6 pb-36 px-6">
                    {/* Currency Selection */}
                    <div className="space-y-1 relative" ref={dropdownRef}>
                        <label className="text-[11px] font-bold text-txt-muted uppercase tracking-wider px-1">{t('wizard.currency')}</label>
                        <div
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="rounded-2xl p-4 flex justify-between items-center cursor-pointer transition-colors"
                            style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-txt-heading font-bold text-lg">{formData.currency}</span>
                                {formData.currency === 'ARS' && (
                                    <span className="text-[10px] font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
                                        {t('wizard.configureRate')}
                                    </span>
                                )}
                            </div>
                            <span className={`material-symbols-outlined text-txt-muted transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>expand_more</span>
                        </div>

                        {/* Dropdown Options */}
                        {isDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-deep-navy rounded-2xl overflow-hidden shadow-xl z-20 animate-in fade-in zoom-in-95 duration-200 max-h-60 overflow-y-auto" style={{ border: '1px solid var(--card-border)' }}>
                                {currencies.map((curr) => (
                                    <div
                                        key={curr}
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, currency: curr }));
                                            setIsDropdownOpen(false);
                                        }}
                                        className={`p-4 hover:bg-surface-hover cursor-pointer flex justify-between items-center ${formData.currency === curr ? 'bg-electric-blue/10' : ''}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`font-bold ${formData.currency === curr ? 'text-electric-blue' : 'text-txt-heading'}`}>{curr}</span>
                                            {curr === 'ARS' && (
                                                <span className="text-[10px] font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
                                                    {t('wizard.configureRate')}
                                                </span>
                                            )}
                                        </div>
                                        {formData.currency === curr && <span className="material-symbols-outlined text-electric-blue text-sm">check</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Exchange Rate Configuration (Only for ARS) */}
                    {formData.currency === 'ARS' && (
                        <div className="space-y-8 animate-in fade-in duration-300">
                            {/* Type of Exchange Toggle */}
                            <div>
                                <label className="text-[11px] font-bold text-txt-muted uppercase tracking-wider px-1 mb-3 block">{t('wizard.exchangeRateType')}</label>

                                <div className="flex flex-col gap-3">
                                    {/* Dolar Cripto Option */}
                                    <div
                                        onClick={() => setFormData(prev => ({ ...prev, exchangeRateType: EXCHANGE_RATE_TYPES.CRIPTO }))}
                                        className={`w-full border rounded-2xl p-4 cursor-pointer transition-all ${formData.exchangeRateType === EXCHANGE_RATE_TYPES.CRIPTO
                                            ? 'border-electric-blue ring-1 ring-electric-blue/50'
                                            : ''
                                            }`}
                                        style={{ background: formData.exchangeRateType === EXCHANGE_RATE_TYPES.CRIPTO ? 'var(--card-bg-hover)' : 'var(--card-bg)', borderColor: formData.exchangeRateType === EXCHANGE_RATE_TYPES.CRIPTO ? undefined : 'var(--card-border)' }}
                                    >
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${formData.exchangeRateType === EXCHANGE_RATE_TYPES.CRIPTO ? 'border-electric-blue bg-electric-blue' : 'border-txt-muted'
                                                }`}>
                                                {formData.exchangeRateType === EXCHANGE_RATE_TYPES.CRIPTO && <div className="w-2 h-2 rounded-full bg-white" />}
                                            </div>
                                            <span className="text-txt-heading font-bold text-[15px]">{t('wizard.dolarCripto')}</span>
                                        </div>

                                        <div className="pl-8">
                                            <div className="text-3xl font-bold text-txt-heading mb-2 tracking-tight">
                                                {dolarCripto ? `${Math.round(dolarCripto.venta)} ARS` : '...'}
                                            </div>
                                            {dolarCripto?.fechaActualizacion && (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                                    <span className="text-xs text-txt-secondary font-medium">
                                                        {t('wizard.updated')} {getUpdatedTimeAgo(dolarCripto.fechaActualizacion)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Dolar Blue Option */}
                                    <div
                                        onClick={() => setFormData(prev => ({ ...prev, exchangeRateType: EXCHANGE_RATE_TYPES.BLUE }))}
                                        className={`w-full border rounded-2xl p-4 cursor-pointer transition-all ${formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE
                                            ? 'border-electric-blue ring-1 ring-electric-blue/50'
                                            : ''
                                            }`}
                                        style={{ background: formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE ? 'var(--card-bg-hover)' : 'var(--card-bg)', borderColor: formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE ? undefined : 'var(--card-border)' }}
                                    >
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE ? 'border-electric-blue bg-electric-blue' : 'border-txt-muted'
                                                }`}>
                                                {formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE && <div className="w-2 h-2 rounded-full bg-white" />}
                                            </div>
                                            <span className="text-txt-heading font-bold text-[15px]">{t('wizard.dolarBlue')}</span>
                                        </div>

                                        <div className="pl-8">
                                            <div className="text-3xl font-bold text-txt-heading mb-2 tracking-tight">
                                                {dolarBlue ? `${Math.round(dolarBlue.venta)} ARS` : '...'}
                                            </div>
                                            {dolarBlue?.fechaActualizacion && (
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                                    <span className="text-xs text-txt-secondary font-medium">
                                                        {t('wizard.updated')} {getUpdatedTimeAgo(dolarBlue.fechaActualizacion)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Adjustment Section */}
                            <div>
                                <label className="text-[11px] font-bold text-txt-muted uppercase tracking-wider px-1 mb-3 block">{t('wizard.rateAdjustment')}</label>

                                <div className="flex items-center justify-between gap-4 mb-2">
                                    <button
                                        onClick={() => handleAdjustmentChange(-0.1)}
                                        className="w-14 h-14 rounded-3xl flex items-center justify-center text-txt-heading transition-all active:scale-95 group"
                                        style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                                    >
                                        <span className="material-symbols-outlined text-2xl group-hover:text-electric-blue transition-colors">remove</span>
                                    </button>

                                    <div className="flex-1 h-14 rounded-3xl flex items-center justify-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                                        <span className={`text-2xl font-bold font-mono tracking-tight ${formData.customExchangeRate > 0 ? 'text-green-400' : formData.customExchangeRate < 0 ? 'text-red-400' : 'text-txt-heading'}`}>
                                            {formData.customExchangeRate > 0 ? '+' : ''}{formData.customExchangeRate.toFixed(1)}%
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => handleAdjustmentChange(0.1)}
                                        className="w-14 h-14 rounded-3xl flex items-center justify-center text-txt-heading transition-all active:scale-95 group"
                                        style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                                    >
                                        <span className="material-symbols-outlined text-2xl group-hover:text-electric-blue transition-colors">add</span>
                                    </button>
                                </div>

                                <p className="text-xs font-medium text-txt-muted text-center px-1">
                                    {t('wizard.rateAdjustmentHint')}
                                </p>
                            </div>

                            {/* Final Rate Info */}
                            <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--divider)' }}>
                                <div className="flex justify-between items-center">
                                    <div className="flex flex-col">
                                        <span className="text-txt-heading font-bold text-lg mb-0.5">{t('wizard.finalRate')}</span>
                                        <span className="text-sm text-txt-muted font-medium">
                                            {t('wizard.calculatedOver')} {formData.exchangeRateType === EXCHANGE_RATE_TYPES.BLUE ? t('wizard.dolarBlue') : t('wizard.dolarCripto')} {formData.customExchangeRate !== 0 ? `${formData.customExchangeRate > 0 ? '+ ' : ''}${formData.customExchangeRate}%` : ''}
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-4xl font-bold text-electric-blue tracking-tight">
                                            {calculateFinalRate()}
                                        </span>
                                        <span className="text-xl text-txt-muted font-medium">ARS</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions (Sticky glassmorphic bar) */}
                <div className="sticky bottom-0 pt-4 pb-6 px-6 flex gap-3 w-full backdrop-blur-xl" style={{ background: 'var(--sticky-bg)' }}>
                    <button
                        onClick={onBack}
                        className="flex-1 font-bold py-4 rounded-xl transition-all active:scale-95 flex items-center justify-center text-txt-secondary"
                        style={{ background: 'var(--card-bg)' }}
                    >
                        {t('wizard.back')}
                    </button>
                    <button
                        onClick={onNext}
                        className="flex-[2] bg-electric-blue hover:bg-electric-blue/90 text-white font-bold py-4 rounded-xl shadow-lg shadow-electric-blue/20 transition-all active:scale-95 flex items-center justify-center"
                    >
                        {t('wizard.continue')}
                    </button>
                </div>
            </div>
        </div>
    );
}
